import type { EntityBody } from "@cloudnest/stone-flicking-core-engine/types";
import type { AsfCanvasTokens } from "./asf-token-bridge";
import { getStonePalette, getZoneTint } from "./asf-token-bridge";
import {
  computeArrowHeadPoints,
  computeIntensityColor,
  computePulseAlpha,
  createRadialGradient,
  drawGlowRing,
  drawShadowedCircle,
} from "./canvas-effects";
import type { CircularCanvasLayout } from "./coordinate-transform";
import { boardToCanvas } from "./coordinate-transform";

function getPlayerIndex(owner: string): number {
  const match = owner.match(/P(\d+)/);
  return match ? Number.parseInt(match[1] ?? "1", 10) - 1 : 0;
}

export function renderBoard(
  ctx: CanvasRenderingContext2D,
  layout: CircularCanvasLayout,
  playerCount: number,
  tokens: AsfCanvasTokens,
): void {
  // Background
  ctx.fillStyle = tokens.boardFill;
  ctx.fillRect(0, 0, layout.canvasWidth, layout.canvasHeight);

  renderSectorZones(ctx, layout, playerCount, tokens);

  // Board circle with subtle radial vignette toward the rim
  const gradient = createRadialGradient(
    ctx,
    layout.centerPixelX,
    layout.centerPixelY,
    layout.pixelRadius * 0.8,
    layout.pixelRadius,
    "rgba(0,0,0,0)",
    tokens.vignette,
  );
  ctx.beginPath();
  ctx.arc(
    layout.centerPixelX,
    layout.centerPixelY,
    layout.pixelRadius,
    0,
    Math.PI * 2,
  );
  ctx.fillStyle = gradient;
  ctx.fill();

  // Re-render sector tints on top so they read above the vignette
  renderSectorZones(ctx, layout, playerCount, tokens);

  // Boundary stroke
  ctx.beginPath();
  ctx.arc(
    layout.centerPixelX,
    layout.centerPixelY,
    layout.pixelRadius,
    0,
    Math.PI * 2,
  );
  ctx.strokeStyle = tokens.boardStroke;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Sector dividers
  if (playerCount > 1) {
    const sectorAngle = (2 * Math.PI) / playerCount;
    ctx.strokeStyle = tokens.sectorDivider;
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 4]);

    for (let i = 0; i < playerCount; i++) {
      const angle = i * sectorAngle + layout.rotationAngle;
      const endX = layout.centerPixelX + layout.pixelRadius * Math.cos(angle);
      const endY = layout.centerPixelY - layout.pixelRadius * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(layout.centerPixelX, layout.centerPixelY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }
}

function renderSectorZones(
  ctx: CanvasRenderingContext2D,
  layout: CircularCanvasLayout,
  playerCount: number,
  tokens: AsfCanvasTokens,
): void {
  const sectorAngle = (2 * Math.PI) / playerCount;

  for (let i = 0; i < playerCount; i++) {
    const startAngle = i * sectorAngle + layout.rotationAngle;
    const endAngle = startAngle + sectorAngle;

    ctx.beginPath();
    ctx.moveTo(layout.centerPixelX, layout.centerPixelY);
    ctx.arc(
      layout.centerPixelX,
      layout.centerPixelY,
      layout.pixelRadius,
      -endAngle,
      -startAngle,
    );
    ctx.closePath();
    ctx.fillStyle = getZoneTint(tokens, i);
    ctx.fill();
  }
}

export function renderEntity(
  ctx: CanvasRenderingContext2D,
  entity: EntityBody,
  layout: CircularCanvasLayout,
  isSelected: boolean,
  tokens: AsfCanvasTokens,
  timestamp?: number,
): void {
  if (entity.eliminated) return;

  const { x, y } = boardToCanvas(entity.x, entity.y, layout);
  const pixelRadius = entity.radius * layout.scale;
  const playerIdx = getPlayerIndex(entity.owner);
  const palette = getStonePalette(tokens, playerIdx);

  // Drop shadow + gradient fill for 3D appearance
  const stoneGradient = createRadialGradient(
    ctx,
    x - pixelRadius * 0.3,
    y - pixelRadius * 0.3,
    pixelRadius * 0.1,
    pixelRadius,
    palette.bg,
    palette.fill,
  );

  drawShadowedCircle(ctx, x, y, pixelRadius, stoneGradient, {
    blur: 4,
    color: "rgba(0,0,0,0.2)",
    offsetY: 2,
  });

  // Stroke
  ctx.beginPath();
  ctx.arc(x, y, pixelRadius, 0, Math.PI * 2);
  ctx.lineWidth = isSelected ? 3 : 2;
  ctx.strokeStyle = isSelected ? tokens.stoneSelectedGlow : palette.fill;
  ctx.stroke();

  // Pulsing glow for selected stone
  if (isSelected) {
    const pulseAlpha = timestamp ? computePulseAlpha(timestamp, 1200) : 0.5;
    drawGlowRing(
      ctx,
      x,
      y,
      pixelRadius + 2,
      tokens.stoneSelectedGlow,
      pulseAlpha,
    );
  }

  // Player number label — high-contrast text on the faction-tinted disc.
  ctx.fillStyle = tokens.onAccent;
  ctx.font = `bold ${Math.round(pixelRadius * 0.85)}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(playerIdx + 1), x, y + 1);
}

export function renderEntities(
  ctx: CanvasRenderingContext2D,
  entities: ReadonlyArray<EntityBody>,
  layout: CircularCanvasLayout,
  selectedEntityId: string | null,
  tokens: AsfCanvasTokens,
  timestamp?: number,
): void {
  for (const entity of entities) {
    renderEntity(
      ctx,
      entity,
      layout,
      entity.id === selectedEntityId,
      tokens,
      timestamp,
    );
  }
}

export function renderFlickPreview(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  layout: CircularCanvasLayout,
  tokens: AsfCanvasTokens,
): void {
  const from = boardToCanvas(fromX, fromY, layout);
  const to = boardToCanvas(toX, toY, layout);

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const maxDist = layout.pixelRadius * 0.8;
  const intensity = Math.min(dist / maxDist, 1);
  const color = computeIntensityColor(intensity, {
    weak: tokens.aimWeak,
    mid: tokens.aimMid,
    strong: tokens.aimStrong,
  });

  // Line with intensity color
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5 + intensity * 1.5;
  ctx.globalAlpha = 0.7;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Arrow head
  const arrowHead = computeArrowHeadPoints(from.x, from.y, to.x, to.y, 12);
  ctx.beginPath();
  ctx.moveTo(arrowHead.tip.x, arrowHead.tip.y);
  ctx.lineTo(arrowHead.p1.x, arrowHead.p1.y);
  ctx.lineTo(arrowHead.p2.x, arrowHead.p2.y);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Impact zone indicator
  if (dist > 20) {
    ctx.beginPath();
    ctx.arc(to.x, to.y, 8 + intensity * 6, 0, Math.PI * 2);
    ctx.fillStyle = `${color}22`;
    ctx.fill();
    ctx.strokeStyle = `${color}44`;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

export function renderReadyIndicators(
  ctx: CanvasRenderingContext2D,
  layout: CircularCanvasLayout,
  playerCount: number,
  readyPlayers: ReadonlyArray<string>,
  tokens: AsfCanvasTokens,
): void {
  const sectorAngle = (2 * Math.PI) / playerCount;
  const indicatorRadius = layout.pixelRadius + 16;

  for (let i = 0; i < playerCount; i++) {
    const playerId = `P${i + 1}`;
    const isReady = readyPlayers.includes(playerId);
    const angle = i * sectorAngle + sectorAngle / 2 + layout.rotationAngle;

    const x = layout.centerPixelX + indicatorRadius * Math.cos(-angle);
    const y = layout.centerPixelY + indicatorRadius * Math.sin(-angle);

    if (isReady) {
      // Filled ready disc with checkmark
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fillStyle = tokens.ready;
      ctx.fill();

      ctx.fillStyle = tokens.onAccent;
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("✓", x, y);
    } else {
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fillStyle = tokens.sectorDivider;
      ctx.fill();
      ctx.strokeStyle = tokens.sectorDivider;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
}
